import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getAdminAreas, createAdminArea, updateAdminArea, deleteAdminArea, getOrganigrama } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { detalleError } from '../../components/admin/detalle'
import { aplanarArbol, descendientes } from '../../utils/arbolAreas'

// Administración > Áreas: the company's areas, each person belongs to any of
// them. The fixed ones (areas.FIJAS: what the code itself filters by) can be
// renamed but not deactivated or deleted; one with people can't be deleted.
// They form a tree (the org chart): each may report to a superior area, and
// its heads (PersonaArea.es_jefe) approve leave for it and every area below.
// One with areas below it can't be deleted either.

// The "Área superior" select: every area in tree order, indented, minus
// `excluir` (the area being edited and everything below it).
function SelectSuperior({ t, filas, excluir, value, onChange, className }) {
  return (
    <select value={value ?? ''} onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
      title={t('areas.superiorHint')} className={className}>
      <option value="">{t('areas.ninguna')}</option>
      {filas.filter(f => !excluir?.has(f.area.id)).map(({ area, nivel }) => (
        <option key={area.id} value={area.id}>
          {`${'\u00A0\u00A0\u00A0'.repeat(nivel)}${area.name}${area.is_active ? '' : ` (${t('areas.inactiva')})`}`}
        </option>
      ))}
    </select>
  )
}

export default function AdminAreas() {
  const { t } = useTranslation(['admin', 'common'])
  const [areas, setAreas] = useState(null)
  const [error, setError] = useState('')
  const [nueva, setNueva] = useState('')
  const [nuevaSuperior, setNuevaSuperior] = useState(null)
  const [creando, setCreando] = useState(false)
  const [editando, setEditando] = useState(null)      // { id, name, parent_id }
  const [jefes, setJefes] = useState({})              // area id -> [{ id, name, role }]

  const cargar = () => {
    getAdminAreas().then(setAreas).catch(() => setError(t('areas.loadFailed')))
    // Heads come from the org chart (active areas only); nice to have.
    getOrganigrama()
      .then(rows => setJefes(Object.fromEntries(rows.map(r => [r.id, r.jefes || []]))))
      .catch(() => {})
  }
  useEffect(() => { cargar() }, [])   // eslint-disable-line react-hooks/exhaustive-deps

  const filas = useMemo(() => aplanarArbol(areas || []), [areas])
  const conHijos = useMemo(() => new Set((areas || []).map(a => a.parent_id).filter(x => x != null)), [areas])

  const crear = async (e) => {
    e.preventDefault()
    if (!nueva.trim()) return
    setCreando(true)
    setError('')
    try {
      await createAdminArea({ name: nueva.trim(), parent_id: nuevaSuperior })
      setNueva('')
      await cargar()
    } catch (err) {
      setError(detalleError(err, t('areas.saveFailed')))
    }
    setCreando(false)
  }

  const guardar = async (a, cambios) => {
    setError('')
    try {
      // PUT replaces parent_id, so it always goes along.
      const out = await updateAdminArea(a.id, { name: a.name, parent_id: a.parent_id ?? null, is_active: a.is_active, ...cambios })
      setAreas(as => as.map(x => x.id === out.id ? out : x))
      return true
    } catch (err) {
      setError(detalleError(err, t('areas.saveFailed')))
      return false
    }
  }

  const editar = async (e, a) => {
    e.preventDefault()
    const name = editando.name.trim()
    if (!name) return
    if (name === a.name && (editando.parent_id ?? null) === (a.parent_id ?? null)) { setEditando(null); return }
    if (await guardar(a, { name, parent_id: editando.parent_id ?? null })) setEditando(null)
  }

  const eliminar = async (a) => {
    if (!window.confirm(t('areas.deleteConfirm', { name: a.name }))) return
    setError('')
    try {
      await deleteAdminArea(a.id)
      setAreas(as => as.filter(x => x.id !== a.id))
    } catch (err) {
      setError(detalleError(err, t('areas.deleteFailed')))
    }
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader title={t('areas.title')} subtitle={t('areas.subtitle')} />
      </div>

      <form onSubmit={crear} className="flex flex-wrap items-center gap-2 mb-2">
        <input value={nueva} onChange={e => setNueva(e.target.value)} placeholder={t('areas.nuevaPlaceholder')}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-72 max-w-full bg-white" />
        <label className="flex items-center gap-2 text-xs text-gray-500">
          {t('areas.superior')}
          <SelectSuperior t={t} filas={filas} value={nuevaSuperior} onChange={setNuevaSuperior}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white max-w-[16rem]" />
        </label>
        <button type="submit" disabled={!nueva.trim() || creando}
          className="bg-gray-900 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40 transition-colors">
          {t('areas.add')}
        </button>
      </form>
      <p className="text-xs text-gray-400 mb-4">{t('areas.superiorHint')}</p>

      {error && <p className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}

      {!areas ? (
        !error && <p className="text-gray-400 text-sm">{t('common:actions.loading')}</p>
      ) : !areas.length ? (
        <p className="text-sm text-gray-400 py-8">{t('areas.empty')}</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto max-w-5xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('areas.columns.name')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('areas.columns.jefes')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('areas.columns.code')}</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500">{t('areas.columns.personas')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('areas.columns.status')}</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filas.map(({ area: a, nivel }) => (
                <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50/60 last:border-b-0 align-top">
                  <td className="px-5 py-3" style={{ paddingLeft: `${1.25 + nivel * 1.5}rem` }}>
                    {editando?.id === a.id ? (
                      <form onSubmit={e => editar(e, a)} className="flex flex-col gap-2"
                        onKeyDown={e => { if (e.key === 'Escape') setEditando(null) }}>
                        <input autoFocus value={editando.name} aria-label={t('areas.nombre')}
                          onChange={e => setEditando(ed => ({ ...ed, name: e.target.value }))}
                          className="border border-gray-200 rounded-lg px-2 py-1 text-sm w-56" />
                        <label className="flex flex-col gap-1 text-xs text-gray-500">
                          {t('areas.superior')}
                          <SelectSuperior t={t} filas={filas} excluir={descendientes(areas, a.id)}
                            value={editando.parent_id} onChange={v => setEditando(ed => ({ ...ed, parent_id: v }))}
                            className="border border-gray-200 rounded-lg px-2 py-1 text-sm w-56 bg-white" />
                        </label>
                        <div className="flex items-center gap-3">
                          <button type="submit" className="text-xs font-medium text-gray-900">{t('common:actions.save')}</button>
                          <button type="button" onClick={() => setEditando(null)} className="text-xs text-gray-400">{t('common:actions.cancel')}</button>
                        </div>
                      </form>
                    ) : (
                      <span className={`font-medium ${a.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
                        {nivel > 0 && <span className="text-gray-300 mr-1.5" aria-hidden="true">└</span>}
                        {a.name}
                        {a.fija && (
                          <span title={t('areas.fijaHint')}
                            className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 align-middle">
                            {t('areas.fija')}
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-600">
                    {(jefes[a.id] || []).length
                      ? jefes[a.id].map(j => <span key={j.id} className="block whitespace-nowrap">★ {j.name}</span>)
                      : <span className="text-gray-300">{a.is_active ? t('areas.sinJefe') : '—'}</span>}
                  </td>
                  <td className="px-5 py-3 text-gray-400 font-mono text-xs">{a.code}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-gray-700">{a.personas}</td>
                  <td className="px-5 py-3">
                    {a.is_active
                      ? <span className="text-xs text-emerald-600 font-medium">{t('areas.activa')}</span>
                      : <span className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-600">{t('areas.inactiva')}</span>}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditando({ id: a.id, name: a.name, parent_id: a.parent_id ?? null })}
                      className="text-xs text-gray-400 hover:text-gray-900 mr-3">{t('areas.editar')}</button>
                    <button onClick={() => guardar(a, { is_active: !a.is_active })}
                      disabled={a.fija && a.is_active}
                      title={a.fija && a.is_active ? t('areas.fijaHint') : undefined}
                      className="text-xs text-gray-400 hover:text-gray-900 mr-3 disabled:opacity-30 disabled:cursor-not-allowed">
                      {a.is_active ? t('areas.desactivar') : t('areas.activar')}
                    </button>
                    <button onClick={() => eliminar(a)}
                      disabled={a.fija || a.personas > 0 || conHijos.has(a.id)}
                      title={a.fija ? t('areas.fijaHint') : a.personas > 0 ? t('areas.conPersonas')
                        : conHijos.has(a.id) ? t('areas.conAreasDebajo') : undefined}
                      className="text-xs text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed">
                      {t('common:actions.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
